import { useEffect } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { FinnLogo } from 'shared/icons/FinnLogo';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SegmentPixel } from 'shared/util/SegmentPixel';
import { SupplierLogo } from '@/components/resultList/images/SupplierLogo';
import type { OfferResponse, PaleVertical } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

const SUPPLIER_NAMES: Record<string, string> = displayValues.suppliers;

const redirectDelay = 3000;
const segmentId = '30128949';

export type OutProps = Omit<OfferResponse, 'numberOfAdults'> & {
    vertical: PaleVertical;
    numberOfAdults: string | null;
    agesOfChildren: string | null;
};

const OutHead = ({ title, supplier }: { title: string; supplier: string }) => (
    <SimpleHead
        title={title}
        canonical="https://www.finn.no/reise/pakkereiser/ut"
        description={`Du forlater nå FINN reise og kan gjennomføre bestillingen hos ${supplier}.`}
    />
);

export const Out = ({ offerId, supplier, numberOfAdults, agesOfChildren, deepLink, vertical }: OutProps) => {
    const { apiUrlPP, apiUrlLMS } = useConfig();

    const redirectUrl = buildRedirectUrl(vertical, deepLink, offerId, apiUrlPP, apiUrlLMS, numberOfAdults, agesOfChildren);

    useEffect((): void => {
        window.setTimeout(() => {
            if (redirectUrl && !window.location.href.match(/\?.*doNotRedirect/)) {
                window.location.replace(redirectUrl);
            }
        }, redirectDelay);
    }, [redirectUrl]);

    const redirectToSupplier = () => (
        <>
            <OutHead title="Du forlater nå FINN reise" supplier={supplier} />
            <div className="max-w-[512px] mx-auto my-64 text-center s-bg-info-subtle rounded-8 p-32">
                <h1 className="t2 font-bold">Du forlater nå FINN reise</h1>
                <section className="my-64">
                    <div className="t3 my-32">
                        <span className="font-bold">Husk:&nbsp;</span>
                        <span data-testid="supplier">{SUPPLIER_NAMES[supplier] ?? supplier}&nbsp;</span>
                        tar i mot bestillingen og må kontaktes dersom du har spørsmål
                    </div>
                    <div className="text-s font-bold my-32">Prisene på FINN reise gjelder per person.</div>
                </section>
                <section className="flex gap-16 justify-between items-center">
                    <FinnLogo size="small" />
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        shapeRendering="geometricPrecision"
                        textRendering="geometricPrecision"
                        viewBox="0 0 256 4"
                        style={{ whiteSpace: 'pre' }}
                        data-testid="progressbar">
                        <style>{'@keyframes a0_w { 0% { width: 10px; } 100% { width: 256px; } }'}</style>
                        <rect width="256" height="4" fill="#DFE4E8" stroke="none" transform="translate(128,2) translate(-128,-2)" />
                        <rect
                            width="10"
                            height="4"
                            fill="#0063FB"
                            stroke="none"
                            transform="translate(71,2) translate(-71,-2)"
                            style={{ animation: '3s linear both a0_w' }}
                        />
                    </svg>
                    <SupplierLogo supplier={supplier} loading="eager" data-testid="supplierLogo" />
                </section>
            </div>
            <SegmentPixel id={segmentId} />
        </>
    );

    const offerDoesNotExistsAnymore = () => (
        <>
            <OutHead title="Tilbudet er ikke tilgjengelig" supplier={supplier} />
            <div className="text-center my-32">
                <h1 className="t2 font-bold" data-testid="offerDoNotExistsText">
                    Du har valgt et tilbud som ikke lenger finnes. Lukk denne siden og prøv et annet tilbud.
                </h1>
            </div>
        </>
    );

    return <Main>{offerId ? redirectToSupplier() : offerDoesNotExistsAnymore()}</Main>;
};

export const buildRedirectUrl = (
    vertical: PaleVertical,
    deepLink: string,
    offerId: string,
    apiUrlPP: string,
    apiUrlLMS: string,
    numberOfAdults?: string | null,
    agesOfChildren?: string | null,
): string => {
    if (vertical === 'pp') {
        const paxParams = `numberOfAdults=${numberOfAdults}${agesOfChildren ? `&agesOfChildren=${agesOfChildren}` : ''}`;
        return `${apiUrlPP}/redirect/${offerId}?${paxParams}`;
    } else if (vertical === 'lms') {
        return `${apiUrlLMS}/redirect/${offerId}`;
    } else {
        return deepLink;
    }
};
